"use client";

/**
 * Sheet — the bottom sheet, for when the screen is a phone.
 *
 * Built on `react-modal-sheet` (MIT) rather than hand-rolled. The drag was the
 * easy part and this file used to do it well; what it did not do is snap
 * points, keyboard avoidance, or the depth effect — the screen behind scaling
 * back as the sheet rises, so the sheet reads as a layer above a surface
 * rather than a panel pasted onto one.
 *
 * WHEN TO REACH FOR IT, which is the decision this page exists to record: a
 * sheet is the phone presentation of a task that suspends the page, and
 * `Modal` is the same task past rail width. They are one decision with two
 * shapes, not two components to choose between by taste — a bottom edge means
 * something on a phone, where that edge is the thumb and where the panel came
 * from, and nothing at all on a desktop.
 *
 * `unstyled`, DELIBERATELY. The library splits its styling in two: `base` is
 * layout it needs to function, `decorative` is a white panel with an 8px
 * radius and its own shadow. Without this flag the decorative half paints over
 * the design system — a white sheet in dark mode with the app's light text on
 * it, which is unreadable rather than merely off-brand. Every visible surface
 * here is a DS token.
 *
 * THE LIBRARY SHIPS NO DIALOG SEMANTICS — no role, no `aria-modal`, no
 * accessible name, no focus trap, no focus restore. It animates and it drags,
 * and everything an overlay owes a keyboard is left to the caller. Without
 * this block, Tab walks straight out of an open sheet into the obscured page
 * behind it and a screen reader never announces the sheet at all.
 *
 * So `useOverlay` still runs here, exactly as it did for the hand-rolled sheet
 * this replaced: it focuses the panel rather than its first control (a sheet
 * that opens on a button invites a reflex Enter), keeps Tab inside, closes on
 * Escape, and puts focus back where it came from. The library's own scroll
 * locking is switched off so one hook owns that too — two things locking
 * scroll fight over restoring it.
 *
 * The visible title is the accessible name via `aria-labelledby`, not a
 * duplicated `aria-label`: one of those goes stale when the other is edited.
 *
 * `contained` IS NOT COSMETIC. The library's root is `position: fixed`, and
 * that is in its `base` styles, so `unstyled` does not remove it. Inside a
 * simulated device the sheet then anchors to the browser window and hangs out
 * of the bottom of the phone. The `style` prop is spread after the library's
 * own, so overriding `position` there is the supported way to contain it.
 */

import { X } from "lucide-react";
import type { ReactNode, Ref, RefObject } from "react";
import { useEffect, useId, useRef } from "react";
import { Sheet as ModalSheet, type SheetRef } from "react-modal-sheet";
import { cn } from "@/lib/utils";
import { Title } from "./text";
import { useOverlay } from "./use-overlay";

/**
 * Held open, a sheet still follows the finger, and then has to come back.
 *
 * The library's drag end, with dismissal disabled and no snap points, leaves
 * the sheet wherever it was let go: half off the screen, with nothing to say
 * it should return. A closed and an open snap point give that branch a place
 * to send it, so a sheet that cannot be dismissed springs back to open, the
 * way a native one does when you pull on something that will not close.
 */
const HOLD_OPEN = [0, 1];

export function Sheet({
  open,
  onClose,
  title,
  titleTrailing,
  children,
  footer,
  header = true,
  contained = false,
  mountPoint,
  depthRootId,
  gutter = "px-nf-ds-card",
  className,
  dismissible = true,
  contentRef,
  onContentScroll,
  modal = true,
  snapPoints,
  initialSnap,
  sheetRef,
}: {
  open: boolean;
  onClose: () => void;
  /** Names the sheet for assistive tech, and titles it unless `header` is off. */
  title: string;
  /**
   * Sits beside the title — a count, a status, whose account this is.
   *
   * The title is a string because it doubles as the accessible name, so there
   * was no way to put anything next to it short of turning the header off and
   * redrawing it, close button and all. Anything here must survive being
   * squeezed: on a phone the header is the title, this, and a 44px close
   * target, so give it a max width and let it truncate.
   */
  titleTrailing?: ReactNode;
  children: ReactNode;
  /** Pinned under the scroll area — where a sheet's one action goes. */
  footer?: ReactNode;
  /** Off when the content draws its own heading. */
  header?: boolean;
  /** Anchor to the nearest positioned ancestor instead of the window. */
  contained?: boolean;
  /** Render inside this element rather than `document.body`. */
  mountPoint?: Element;
  /** Element to scale back as the sheet rises. Must be outside `mountPoint`'s sheet. */
  depthRootId?: string;
  /** Horizontal padding, so a caller with its own gutter can opt out. */
  gutter?: string;
  /**
   * Classes for the panel itself.
   *
   * It exists for one case, which is not decoration: the panel is portalled to
   * `document.body`, so a caller whose own tree wears a token layer — `/one`
   * and its `ds-next` — cannot reach it. Without a way to put that class on
   * the panel the sheet arrives in a different palette from the page that
   * opened it. Surface, radius and shadow stay the design system's.
   */
  className?: string;
  /**
   * False holds the sheet open until the caller's own action closes it:
   * drag, backdrop, Escape and the close button all stand down, and the
   * close button is not drawn, since a control that does nothing is a lie.
   *
   * For a sheet whose whole purpose is that it gets finished, like an
   * agreement that must be read to the end. Everywhere else leave it on:
   * a sheet you cannot leave is a trap, and the reason has to be on screen.
   */
  dismissible?: boolean;
  /** The scroll area, for a caller that needs to know where the reader is. */
  contentRef?: RefObject<HTMLDivElement | null>;
  onContentScroll?: (element: HTMLDivElement) => void;
  /**
   * False makes the sheet a panel beside the page rather than a task over
   * it: no scrim, no focus trap, no scroll lock, no recede, and a `region`
   * rather than a `dialog`. The page behind stays live.
   *
   * The guideline's own exception: an overlay dims only when the task is
   * suspended, and a parallel, non-blocking panel takes offset without a
   * scrim. For a thread beside a video that keeps playing, or a tray of
   * controls the page is still being used around. The drag, the snap and
   * the surface are the same object; only the contract with the page
   * changes. Pair it with `snapPoints` so the panel has a height the page
   * can lay itself out against.
   */
  modal?: boolean;
  /**
   * Heights the sheet rests at, ascending, `0` for closed: a fraction of the
   * sheet's full height (0 to 1) or a pixel value, as the library reads
   * them. `[0, 0.62]` is a panel that opens to 62% and can be dragged shut.
   * Without them the sheet sizes to its content and only opens or closes.
   */
  snapPoints?: number[];
  /** Which of `snapPoints` the sheet opens at; the fullest unless told. */
  initialSnap?: number;
  /**
   * The library's own handle: `y` and `yInverted` are motion values of the
   * sheet's position, `snapTo` moves it. For a page that lays itself out
   * against the sheet as it moves, which is what a panel beside live content
   * has to do, frame by frame, rather than on a timer of its own.
   */
  sheetRef?: Ref<SheetRef>;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const requestClose = dismissible ? onClose : () => {};
  // A panel does not trap, lock or restore: the page behind it is still the
  // page, and taking its focus and scroll would make the panel a dialog by
  // another name.
  useOverlay({ open: open && modal, onClose: requestClose, ref: panel });

  // Marks the page element so `globals.css` can cancel the library's own
  // 500ms transition on it — see the note there. Without it the recede chases
  // the sheet instead of tracking it, and detaches from it on the way out.
  // A class rather than an inline style because the library re-writes its
  // inline transition on every `animationStart`, so only a stylesheet rule
  // with `!important` reliably wins.
  useEffect(() => {
    if (!depthRootId || !modal) return;
    const root = document.getElementById(depthRootId);
    if (!root) return;
    root.classList.add("nf-sheet-depth-root");
    return () => root.classList.remove("nf-sheet-depth-root");
  }, [depthRootId, modal]);

  return (
    <ModalSheet
      ref={sheetRef}
      isOpen={open}
      onClose={requestClose}
      disableDismiss={!dismissible}
      snapPoints={snapPoints ?? (dismissible ? undefined : HOLD_OPEN)}
      initialSnap={
        snapPoints ? (initialSnap ?? snapPoints.length - 1) : undefined
      }
      // Given snap points the sheet is sized by them against the window;
      // otherwise it is as tall as what it holds.
      detent={snapPoints ? "full" : "content"}
      unstyled
      // `useOverlay` locks the page; two things doing it fight over the
      // restore.
      disableScrollLocking
      mountPoint={mountPoint}
      modalEffectRootId={modal ? depthRootId : undefined}
      // Spread after the library's `base`, which is how its `position: fixed`
      // gets overridden — see the note above.
      style={contained ? { position: "absolute" } : undefined}
    >
      {modal ? (
        <ModalSheet.Backdrop
          onTap={requestClose}
          className="bg-nf-ds-ink/40 backdrop-blur-[2px]"
        />
      ) : null}
      <ModalSheet.Container
        ref={panel}
        role={modal ? "dialog" : "region"}
        aria-modal={modal ? "true" : undefined}
        // Point at the visible heading where there is one, and fall back to the
        // string only when the caller draws its own.
        aria-labelledby={header ? titleId : undefined}
        aria-label={header ? undefined : title}
        // Focusable so the hook can put focus on the panel itself.
        tabIndex={-1}
        className={cn(
          "flex flex-col overflow-hidden bg-nf-ds-surface-card text-nf-ds-fg",
          "rounded-t-nf-ds-card",
          "shadow-[0_-1px_2px_rgba(14,14,16,0.06),0_-16px_40px_-12px_rgba(14,14,16,0.3)]",
          "dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_-16px_40px_-8px_rgba(0,0,0,0.7)]",
          className,
        )}
      >
        <ModalSheet.Header>
          {/* The grab area is the whole header strip, not the 40px grabber: a
              5px-tall target is a target nobody hits. */}
          <div className="flex flex-col items-center gap-nf-ds-2 pt-nf-ds-2 pb-nf-ds-1">
            <span
              aria-hidden="true"
              className="h-[5px] w-[40px] rounded-nf-ds-pill bg-nf-ds-fg/20"
            />
            {header ? (
              <div
                className={cn(
                  "flex w-full items-center justify-between gap-nf-ds-item pb-nf-ds-1",
                  gutter,
                )}
              >
                <span className="flex min-w-0 items-center gap-nf-ds-2">
                  <Title id={titleId} className="shrink-0">
                    {title}
                  </Title>
                  {titleTrailing}
                </span>
                {dismissible ? (
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="-mr-nf-ds-2 grid size-[44px] shrink-0 cursor-pointer place-items-center rounded-nf-ds-pill text-muted-foreground outline-hidden transition-transform duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:ring-2 focus-visible:ring-nf-ds-brand active:scale-[0.92] motion-reduce:transition-none"
                  >
                    <X className="size-[20px]" strokeWidth={2} />
                  </button>
                ) : (
                  // Holds the close button's place while it is withheld, so
                  // the title does not shift when it arrives.
                  <span
                    aria-hidden="true"
                    className="-mr-nf-ds-2 size-[44px] shrink-0"
                  />
                )}
              </div>
            ) : null}
          </div>
        </ModalSheet.Header>

        {/*
          The library's own scroller IS the scroll area, not a box nested in
          it. Two reasons, both of which broke a long sheet outright:

          - It sizes itself `height: 100%` against a container that is
            `height: auto` under the `content` detent, which cannot resolve,
            so it grew to the whole content and the capped container clipped
            it: nothing scrolled and the footer sat below the screen. `flex`
            with `min-height: 0` takes the space the cap leaves instead.
          - While the sheet can be dragged it sets `touch-action: pan-down`
            on that scroller, and touch-action applies to everything inside
            it, so a nested scroller could not be scrolled up by touch. The
            library tracks its own element's scroll position and only drags
            from the top, which is the behaviour we wanted anyway.

          Scroll does not bubble, so it is reported from the capture phase.
        */}
        <ModalSheet.Content
          scrollRef={contentRef}
          scrollStyle={{ height: "auto", flex: "1 1 auto", minHeight: 0 }}
          scrollClassName="overscroll-contain"
          onScrollCapture={
            onContentScroll
              ? (event: React.UIEvent<HTMLDivElement>) => {
                  if (event.target instanceof HTMLDivElement)
                    onContentScroll(event.target);
                }
              : undefined
          }
        >
          <div className={cn("pb-nf-ds-6", gutter)}>{children}</div>
        </ModalSheet.Content>
        {/* Outside the scroller, so the sheet's one action never scrolls
            away from the thumb. */}
        {footer ? (
          <div
            className={cn(
              "shrink-0 border-t border-nf-ds-border pt-nf-ds-item",
              gutter,
            )}
            // Same bottom inset as every other piece of bottom chrome: a
            // sheet reaches the screen's edge, so it owes that edge the same
            // clearance. Falls back to the gutter where no frame sets it.
            style={{
              paddingBottom: "calc(var(--one-inset, 12px) + 12px)",
            }}
          >
            {footer}
          </div>
        ) : null}
      </ModalSheet.Container>
    </ModalSheet>
  );
}

export type SheetMountPoint = RefObject<HTMLElement | null>;
export type { SheetRef };
